import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, X } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { useCart } from "../context/CartContext";
import { fmtPrice } from "../lib/format";
import { resolveImg } from "../lib/api";
import { useCartRestore } from "../hooks/useCartRestore";

export default function Cart() {
  const { t, lang, money } = useLang();
  const { items, updateQty, removeItem, subtotal } = useCart();
  const navigate = useNavigate();
  useCartRestore();

  if (items.length === 0) {
    return (
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-32 text-center">
        <h1 className="serif text-4xl mb-8">{t("your_bag")}</h1>
        <p className="text-[#8A857D] mb-10">{t("empty_bag")}</p>
        <Link to="/shop" className="inline-block bg-[#C49A6C] text-[#0B0A08] text-xs uppercase tracking-[0.25em] px-9 py-4 hover:bg-[#E7CBA9] transition-colors">{t("continue_shopping")}</Link>
      </div>
    );
  }

  return (
    <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-16 md:py-24">
      <h1 className="serif text-4xl md:text-5xl mb-12">{t("your_bag")}</h1>
      <div className="grid lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2 space-y-8">
          {items.map((i) => (
            <div key={i.product_id} className="flex gap-6 border-b border-[#262421] pb-8" data-testid={`cart-page-item-${i.slug}`}>
              <Link to={`/product/${i.slug}`} className="w-28 h-36 bg-[#141311] flex-shrink-0 overflow-hidden">
                {i.image && <img src={resolveImg(i.image)} alt="" className="w-full h-full object-cover" />}
              </Link>
              <div className="flex-1 flex flex-col">
                <div className="flex justify-between gap-4">
                  <div>
                    <h3 className="serif text-2xl">{lang === "ar" ? i.name_ar || i.name_en : i.name_en}</h3>
                    <p className="text-xs uppercase tracking-widest text-[#8A857D] mt-2">{i.size}</p>
                  </div>
                  <button onClick={() => removeItem(i.product_id)} data-testid={`cart-page-remove-${i.slug}`} className="text-[#6E685F] hover:text-[#c88] transition-colors h-fit"><X size={18} /></button>
                </div>
                <div className="flex items-center justify-between mt-auto pt-6">
                  <div className="flex items-center border border-[#262421]">
                    <button onClick={() => updateQty(i.product_id, i.qty - 1)} className="px-3 py-2 text-[#8A857D] hover:text-[#F2EBE1]"><Minus size={14} /></button>
                    <span className="px-4 text-sm">{i.qty}</span>
                    <button onClick={() => updateQty(i.product_id, i.qty + 1)} className="px-3 py-2 text-[#8A857D] hover:text-[#F2EBE1]"><Plus size={14} /></button>
                  </div>
                  <span className="serif text-xl text-[#C49A6C]">{money(i.price * i.qty)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="lg:col-span-1">
          <div className="bg-[#0F0F0F] border border-[#262421] p-8 lg:sticky lg:top-24">
            <h2 className="text-xs uppercase tracking-[0.2em] text-[#8A857D] mb-6">{t("order_summary")}</h2>
            <div className="flex justify-between py-3 text-sm">
              <span className="text-[#8A857D]">{t("subtotal")}</span>
              <span>{money(subtotal)}</span>
            </div>
            <div className="flex justify-between py-3 text-sm border-b border-[#262421]">
              <span className="text-[#8A857D]">{t("shipping")}</span>
              <span className="text-[#6E685F] text-xs uppercase tracking-widest">{t("calculated_checkout")}</span>
            </div>
            <div className="flex justify-between py-5">
              <span className="serif text-lg">{t("total")}</span>
              <span className="serif text-lg text-[#C49A6C]">{money(subtotal)}</span>
            </div>
            <button onClick={() => navigate("/checkout")} data-testid="cart-page-checkout" className="w-full bg-[#C49A6C] text-[#0B0A08] text-xs uppercase tracking-[0.25em] py-4 hover:bg-[#E7CBA9] transition-colors">
              {t("checkout")}
            </button>
            <Link to="/shop" className="block text-center mt-4 text-xs uppercase tracking-[0.2em] text-[#8A857D] hover:text-[#F2EBE1] transition-colors">{t("continue_shopping")}</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
